import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid,
} from 'recharts';
import { fmtMoney, fmtNumber } from './format';
import DataDisclosure from './DataDisclosure';
import { c, t, sp, r, chart, tbl } from '../../theme';

// Value by how long stock has sat, in place of the single twelve-month cut the
// export ships. $400k idle two years is a different decision from $400k idle
// thirteen months, and the one-cut version cannot tell them apart.
//
// Color runs light→dark with age, so the chart reads as a ramp rather than as
// five unrelated categories — length is the message, and `sequential` is the
// set built for magnitude. "Never used" sits outside the ramp in the warning
// tone: it is not simply the oldest band, it is stock with no recorded issue
// at all, which is a different claim about the part.
const BANDS = {
  '0_6':     { label: '0–6 mo',    fill: chart.sequential[0] },
  '6_12':    { label: '6–12 mo',   fill: chart.sequential[1] },
  '12_24':   { label: '12–24 mo',  fill: chart.sequential[2] },
  '24_plus': { label: '24+ mo',    fill: chart.sequential[3] },
  never:     { label: 'Never used',  fill: c.warning },
};

export default function AgeBandChart({ bands, onSelect, selected }) {
  const rows = (bands || []).map((b) => ({
    ...b,
    label: BANDS[b.band]?.label || b.band,
    fill:  BANDS[b.band]?.fill  || chart.other,
  }));
  const total = rows.reduce((sum, b) => sum + Number(b.value || 0), 0);
  if (!total) return null;

  return (
    <div>
      <div style={s.head}>
        <div style={s.title}>Value by Age</div>
        <div style={s.sub}>Time since the part was last issued{onSelect ? ' — select a band to list the parts' : ''}</div>
      </div>

      <div style={s.chartWrap}>
        <ResponsiveContainer width="100%" height={190}>
          <BarChart data={rows} margin={{ top: 8, right: 16, bottom: 4, left: 8 }}>
            <CartesianGrid vertical={false} stroke={chart.grid} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: chart.axisText }} axisLine={false} tickLine={false} />
            <YAxis
              tickFormatter={fmtMoney}
              tick={{ fontSize: 10, fill: chart.axisText }}
              axisLine={false} tickLine={false} width={64}
            />
            <Tooltip
              cursor={{ fill: 'rgba(27,42,74,0.05)' }}
              formatter={(v, _n, p) => [
                `${fmtMoney(v)} · ${fmtNumber(p?.payload?.item_count)} lines · ${Math.round((v / total) * 100)}%`,
                'Value',
              ]}
              contentStyle={{ fontSize: '12px', borderRadius: r.lg, border: `1px solid ${chart.tooltipBorder}` }}
            />
            <Bar
              dataKey="value"
              radius={[4, 4, 0, 0]}
              isAnimationActive={false}
              onClick={onSelect ? (d) => onSelect(d.band === selected ? null : d.band) : undefined}
              style={onSelect ? { cursor: 'pointer' } : undefined}
            >
              {rows.map((b) => (
                <Cell
                  key={b.band}
                  fill={selected && selected !== b.band ? chart.deselectedLight : b.fill}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* The chart plots value only. Line counts and the share of the total are
          in the data and nowhere on the marks, and the ratio between them is
          the point: a band holding 8% of the value across 35% of the lines is
          cheap clutter, the reverse is concentrated capital. */}
      <DataDisclosure label="age band" count={rows.length}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Age Band</th>
              <th style={s.thR}>Part Lines</th>
              <th style={s.thR}>Distinct Parts</th>
              <th style={s.thR}>Value</th>
              <th style={s.thR}>Share</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.band} style={s.tr}>
                <td style={s.td}>
                  <span style={{ ...s.swatch, background: row.fill }} />
                  {row.label}
                </td>
                <td style={s.tdR}>{fmtNumber(row.item_count)}</td>
                <td style={s.tdR}>{fmtNumber(row.distinct_part_count)}</td>
                <td style={s.tdR}>{fmtMoney(row.value)}</td>
                <td style={s.tdR}>{((Number(row.value || 0) / total) * 100).toFixed(1)}%</td>
              </tr>
            ))}
            <tr style={s.trTotal}>
              <td style={s.td}>Total</td>
              <td style={s.tdR}>{fmtNumber(rows.reduce((n, b) => n + Number(b.item_count || 0), 0))}</td>
              {/* Not a total: the same part number can sit in two bands when it
                  is held at two yards with different last-used dates. Lines add
                  up, parts do not. */}
              <td style={{ ...s.tdR, color: c.textFaint }} title="Distinct parts cannot be summed across bands — one part number can appear in more than one.">—</td>
              <td style={s.tdR}>{fmtMoney(total)}</td>
              <td style={s.tdR}>100.0%</td>
            </tr>
          </tbody>
        </table>
      </DataDisclosure>
    </div>
  );
}

const s = {
  head:      { padding: `${sp[4]} ${sp[5]} 0`, display: 'flex', alignItems: 'baseline', gap: sp[3], flexWrap: 'wrap' },
  title:     { ...t.h3, color: c.navy },
  sub:       { ...t.micro, color: c.textFaint },
  chartWrap: { padding: `${sp[2]} ${sp[5]} ${sp[1]}` },

  table:   { width: '100%', borderCollapse: 'collapse' },
  th:      { ...tbl.th,  whiteSpace: 'nowrap' },
  thR:     { ...tbl.thR, whiteSpace: 'nowrap' },
  tr:      { ...tbl.tr },
  td:      { ...tbl.td,  whiteSpace: 'nowrap' },
  tdR:     { ...tbl.tdR },
  trTotal: { ...tbl.tr, background: c.surfaceSunk, fontWeight: 600 },
  // The swatch is what ties a row to its bar; without it the table is a second
  // list the reader has to match up by name.
  swatch:  { display: 'inline-block', width: '9px', height: '9px', borderRadius: '2px', marginRight: sp[2] },
};
